import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { DocChronologyDownloadColumn } from '../../../../backend/src/case/chronology/doc-chronology/doc-chronology-download';
import { DownloadWithDocsResponseDto } from '../../../../backend/src/case/chronology/doc-chronology/dto/download-with-docs-response.dto';
import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UseDocChronologyDownloadProps {
  docChronologyId: string;
  matterId: string;
  subsetId: string;
}

const useDocChronologyDownload = ({ docChronologyId, matterId, subsetId }: UseDocChronologyDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadDocChronology = async ({
    styleFormat,
    title,
    columns,
    relativityColumns,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments = false,
  }: {
    styleFormat: StyleFormat;
    downloadFormat: DownloadFormat;
    title: string;
    columns?: DocChronologyDownloadColumn[];
    relativityColumns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    includeSourceDocuments?: boolean;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        relativityColumns,
        style,
        documentOptions,
      },
    });

    if (includeSourceDocuments) {
      // Start async job and return job info
      const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/download-with-documents`;
      const response = await fetch(endpoint, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to start document chronology export');
      }

      const data: DownloadWithDocsResponseDto = await response.json();
      return data;
    } else {
      // Synchronous download
      const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/download`;
      const downloadResult = await fetch(endpoint, fetchConfig);

      if (!downloadResult.ok) {
        throw new Error('Failed to download document chronology');
      }

      const blob = await downloadResult.blob();
      const link = document.createElement('a');
      const file = new Blob([blob], { type: 'text/plain' });
      link.href = URL.createObjectURL(file);
      link.download = `${title}.${downloadFormat}`;
      link.click();
      URL.revokeObjectURL(link.href);

      return null;
    }
  };

  const downloadDocChronologyWithDocuments = async (title: string): Promise<void> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.GET,
    });

    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/export-download-url`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to get document chronology export download URL');
    }

    const data: { downloadUrl: string } = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = `${title} with source documents.zip`;
    link.click();
  };

  return {
    downloadMutation: useMutation(downloadDocChronology, {
      onError: () => {
        toast.error('Failed to download document chronology. Please try again.');
      },
    }),
    downloadWithDocuments: downloadDocChronologyWithDocuments,
  };
};

export default useDocChronologyDownload;
